import { AnimatePresence, motion, usePresenceData } from 'framer-motion';
import { use, useCallback, useState } from 'react';
import { Location, NavigateFunction } from 'react-router';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { OnboardingPages } from '../../../common/src/pages.ts';
import { ensureUnreachable } from '../../../common/src/util.ts';
import BackArrowIcon from '../assets/arrow-previous-left-icon.svg?react';
import { CloseButton } from '../util/shared.tsx';
import { AllowNotifications } from './onboarding/allow_notifications.tsx';
import { AppContext, AppContextProvider, PageAnimation } from './onboarding/app_context.tsx';
import { ChooseUser } from './onboarding/choose_user.tsx';
import { Demo } from './onboarding/demo.tsx';
import { Download } from './onboarding/download.tsx';
import { navigateBack } from './onboarding/frame.tsx';
import { HouseholdCreate } from './onboarding/household_create.tsx';
import { HouseholdEmail } from './onboarding/household_email.tsx';
import { HouseholdFind } from './onboarding/household_find.tsx';
import { HouseholdFirstName } from './onboarding/household_first_name.tsx';
import { HouseholdJoin } from './onboarding/household_join.tsx';
import { HouseholdLastName } from './onboarding/household_last_name.tsx';
import { HouseholdNotFound } from './onboarding/household_not_found.tsx';
import { HouseholdStart } from './onboarding/household_start.tsx';
import { HouseholdWelcome } from './onboarding/household_welcome.tsx';
import { OrgChoice } from './onboarding/org_choice.tsx';
import { ProgressBar } from './onboarding/progress_bar.tsx';
import { ShareMusicLeader } from './onboarding/share_music_leader.tsx';
import { SubscribeFindHousehold } from './onboarding/subscribe_find_household.tsx';
import { SubscribePayment } from './onboarding/subscribe_payment.tsx';
import { SubscribeStart } from './onboarding/subscribe_start.tsx';
import { SubscribeThankYou } from './onboarding/subscribe_thank_you.tsx';
import { SurveyDiscovery } from './onboarding/survey_discovery.tsx';
import { SurveyHouseholdSchedule } from './onboarding/survey_household_schedule.tsx';

export const OnboardingPage = () => {
  return (
    <AppContextProvider>
      <PageTransitionWrapper />
    </AppContextProvider>
  );
};

const PageTransitionWrapper = () => {
  const location = useLocation();
  const page = getOnboardingPage(location);
  const appContext = use(AppContext);
  const navigate = useNavigate();
  const [followupTimer, setFollowupTimer] = useState<ReturnType<typeof setTimeout> | undefined>();
  const onBackCallback = useCallback(() => {
    if (followupTimer) {
      clearTimeout(followupTimer);
    }
    navigateBack(appContext, navigate, appContext?.onBack);

    // wait for the page transition to be done before reversing the transition direction
    // note that using setTimeout in the Button's onClick handler fixes this but breaks iOS input focus
    setFollowupTimer(setTimeout(() => appContext?.setPageAnimation(PageAnimation.RightToLeft), 700));
  }, [appContext, followupTimer, navigate, setFollowupTimer]);

  if (!appContext) {
    return null;
  }

  // TODO(hewitt): AnimatePresence mode="sync" fails to perform both the enter & exit transition
  //               AnimatePresence mode="wait" does both the enter & exit transition, but creates blank space between
  //               We decided to remove the exit transition for now so that the enter transition occurs in "sync"
  return (
    <div>
      {!appContext.suppressBackButton && (
        <BackButton key="back" aria-label="Back" onClick={onBackCallback}>
          <LargeBackArrow />
        </BackButton>
      )}
      <ProgressBar page={page} />
      {appContext.onClose && <CloseButton key="close" onClick={appContext.onClose} />}
      <AnimatePresence mode="sync" initial={false} custom={{ pageAnimation: appContext.pageAnimation }}>
        <SlideInPage key={page} page={page} />
      </AnimatePresence>
    </div>
  );
};

function SlideInPage({ page }: { page: OnboardingPages }) {
  const { pageAnimation } = usePresenceData() as { pageAnimation: PageAnimation };
  const initialX = pageAnimation === PageAnimation.RightToLeft ? '100%' : '-100%';
  const navigate = useNavigate();
  // TODO(hewitt): Use the code below to restore the exit transition when "sync" works properly (see comment above)
  // const exitX = pageAnimation === PageAnimation.RightToLeft ? '-100%' : '100%';
  //       exit={{ x: exitX, transition: { duration: 0.25, ease: 'easeOut' } }}>
  return (
    <motion.div initial={{ x: initialX }} animate={{ x: 0, transition: { duration: 0.25, ease: 'easeOut' } }}>
      {renderOnboardingPage(page, navigate)}
    </motion.div>
  );
}

export function getOnboardingPage(location: Location): OnboardingPages {
  const pageName = location.hash && location.hash.slice(1);
  if (pageName && Object.values(OnboardingPages).includes(pageName as OnboardingPages)) {
    return pageName as OnboardingPages;
  }
  return OnboardingPages.Demo;
}

export function renderOnboardingPage(page: OnboardingPages, navigate: NavigateFunction) {
  switch (page) {
    case OnboardingPages.AllowNotifications:
      return <AllowNotifications />;
    case OnboardingPages.OrgChoice:
      return <OrgChoice />;
    case OnboardingPages.Demo:
      return <Demo />;
    case OnboardingPages.SurveyDiscovery:
      return <SurveyDiscovery />;
    case OnboardingPages.SurveyHouseholdSchedule:
      return <SurveyHouseholdSchedule />;
    case OnboardingPages.HouseholdStart:
      return <HouseholdStart />;
    case OnboardingPages.HouseholdLastName:
      return <HouseholdLastName />;
    case OnboardingPages.HouseholdEmail:
      return <HouseholdEmail />;
    case OnboardingPages.HouseholdFirstName:
      return <HouseholdFirstName />;
    case OnboardingPages.HouseholdNotFound:
      return <HouseholdNotFound />;
    case OnboardingPages.HouseholdCreate:
      return <HouseholdCreate />;
    case OnboardingPages.HouseholdJoin:
      return <HouseholdJoin />;
    case OnboardingPages.HouseholdFind:
      return <HouseholdFind />;
    case OnboardingPages.HouseholdWelcome:
      return <HouseholdWelcome />;
    case OnboardingPages.ShareMusicLeader:
      return <ShareMusicLeader />;
    case OnboardingPages.SubscribeStart:
      return <SubscribeStart />;
    case OnboardingPages.SubscribeFindHousehold:
      return <SubscribeFindHousehold />;
    case OnboardingPages.SubscribePayment:
      return <SubscribePayment />;
    case OnboardingPages.SubscribeThankYou:
      return <SubscribeThankYou />;
    case OnboardingPages.Download:
      return <Download />;
    case OnboardingPages.ChooseUser:
      return <ChooseUser navigate={navigate} />;
    default:
      ensureUnreachable(page);
  }
}

export const BackButton = styled.button`
  z-index: 1; // TODO(hewitt): should not be necessary - wasn't when in frame.ts
  position: absolute;
  top: 18px;
  left: 22px;
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const LargeBackArrow = styled(BackArrowIcon)`
  fill: var(--color-text-light);
  height: 30px;
`;
